import { useEffect } from "react";
import { reaction } from "mobx";

import { supabase } from "@/integrations/supabase/client";
import { useChatStore } from "@/hooks/useChatStore";

/**
 * How often the tracked documents are re-read while any of them is still
 * pre-extraction. Realtime is the fast path; this is the guarantee — a missed
 * UPDATE would otherwise leave the chip spinning and the message it carries
 * unsent for good.
 */
export const ATTACHMENT_STATE_POLL_MS = 5000;

/** Sequence for channel topics, so no two subscriptions ever share one. */
let channelSeq = 0;

/**
 * WHA-3356: keep the `state` field on every attached document chip in sync
 * with `public.documents.state` while the pipeline runs.
 *
 * Strategy: when the set of attached doc ids changes, subscribe to the
 * documents table via Supabase Realtime and fan UPDATE events back into the
 * ChatStore. On unmount or when the attachment set empties, tear the
 * channel down.
 *
 * We re-subscribe (not patch the filter) because PostgREST channel filters
 * are immutable once subscribed — the cost of resubscription is a single
 * websocket round-trip and is dwarfed by the user-driven cadence of
 * attaching files.
 *
 * Realtime is never the source of truth here: `postgres_changes` has no
 * replay, so an UPDATE landing before the channel has joined, or while the
 * socket is down, is gone. The documents are re-read from the table on every
 * SUBSCRIBED and on a poll while any of them is still pre-extraction — the
 * extraction usually finishes within seconds, often before the join.
 *
 * Every subscription gets its own topic. Sending a message empties the
 * composer and parks the same ids on `queuedSend` a moment later, so the set
 * goes [A] → [] → [A] in one tick; `supabase.channel()` returns any channel
 * still on the socket under the same topic, which at that moment is the one
 * just told to leave. Subscribing it is a no-op, the leave then closes it,
 * and the chip spun forever with its message never sent.
 *
 * WHA-3330: the chip state is reported AS-IS from documents.state. We used
 * to verify pages-exist via an extra RPC before flipping the chip out of
 * "extracting", but that round-trip made the chip lag (and got stuck in
 * one user-reported case). The optimistic-send queue in ChatStore handles
 * the "is the chatbot allowed to receive this message yet" question
 * separately, so the chip can just mirror state. Chip clears the instant
 * the realtime UPDATE arrives.
 *
 * WHA-3330 (chip-moves-to-bubble): we also track doc ids from any
 * in-flight queuedSend, not just the input area. After the user hits Send
 * the chip "moves" from above the input into the user-message bubble (via
 * DocChipGroup parsing the [ATTACHED DOCUMENTS] header). Both surfaces
 * read their loading state from `chatStore.loadingDocIds`, which the
 * subscription below keeps current.
 */
export function useChatbotAttachmentStateSync() {
  const chatStore = useChatStore();

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let currentKey = "";
    let currentIds: number[] = [];
    let disposed = false;

    const applyDocState = (docId: number, state: string | null) => {
      const idStr = String(docId);
      // Input chip state (no-op if the chip isn't present).
      chatStore.setAttachedDocumentState(idStr, state);
      // Per-id loading flag (read by user-message DocChipGroup).
      chatStore.setDocLoading(idStr, chatStore.isPreExtractionState(state));
    };

    const stopPolling = () => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
    };

    const applySnapshot = (ids: number[], rows: { id: number; state: string | null }[]) => {
      const seen = new Set<number>();
      for (const row of rows) {
        seen.add(row.id);
        applyDocState(row.id, row.state ?? null);
      }
      // A row the user cannot read (deleted, or moved out of reach) will never
      // finish extracting. Holding its message for it would hold it forever;
      // the chatbot reports a document it cannot open itself.
      for (const id of ids) {
        if (!seen.has(id)) applyDocState(id, null);
      }
      if (!ids.some((id) => chatStore.isDocLoading(String(id)))) stopPolling();
    };

    /** Read the tracked documents' current state straight from the table. */
    const refresh = async (ids: number[]) => {
      const { data, error } = await supabase.from("documents").select("id, state").in("id", ids);
      // A read that lands after the set moved on describes ids nobody tracks.
      if (disposed || ids !== currentIds) return;
      if (error) return; // The next poll or SUBSCRIBED retries.
      applySnapshot(ids, data ?? []);
    };

    const reSubscribe = (ids: number[]) => {
      const key = ids.slice().sort().join(",");
      if (key === currentKey) return;
      currentKey = key;
      currentIds = ids;

      stopPolling();
      if (channel) {
        void supabase.removeChannel(channel);
        channel = null;
      }

      if (ids.length === 0) return;

      // Seed current state immediately so spinners reflect the latest
      // snapshot even if the realtime subscription is slow to settle.
      void refresh(ids);
      pollTimer = setInterval(() => void refresh(ids), ATTACHMENT_STATE_POLL_MS);

      channel = supabase
        .channel(`chatbot-attachment-states-${key}-${++channelSeq}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "documents",
            filter: `id=in.(${ids.join(",")})`,
          },
          (payload) => {
            const row = payload.new as { id: number; state: string | null };
            applyDocState(row.id, row.state);
          }
        )
        .subscribe((status) => {
          // Anything that changed before the join (or during a reconnect) was
          // never delivered; read it now.
          if (status === "SUBSCRIBED") void refresh(ids);
        });
    };

    const stop = reaction(
      () => {
        // Union of ids we need state for: input chips + queued-send chips
        // (which have already been removed from the input area but still
        // need their loading state tracked so the message bubble can
        // render correctly).
        const inputIds = chatStore.attachedDocuments
          .map((d) => Number(d.id))
          .filter((n) => Number.isFinite(n));
        const queuedIds = (chatStore.queuedSend?.attachedDocIds ?? [])
          .map((s) => Number(s))
          .filter((n) => Number.isFinite(n));
        // A send that went out at its deadline leaves its documents still
        // processing; their chips spin on in the bubble until they finish.
        const loadingIds = Array.from(chatStore.loadingDocIds)
          .map((s) => Number(s))
          .filter((n) => Number.isFinite(n));
        // Dedup.
        return Array.from(new Set([...inputIds, ...queuedIds, ...loadingIds]));
      },
      (ids) => reSubscribe(ids),
      { fireImmediately: true }
    );

    // WHA-3330: drain the queued-send buffer the moment every attached doc
    // has finished extracting. submitMessage stashes deferred SSE params in
    // chatStore.queuedSend when the user submits while attachments are
    // still pre-extraction; here we react to attachment state changes and
    // flush the queue as soon as the gate clears.
    const stopFlushReaction = reaction(
      () => {
        const queued = chatStore.queuedSend;
        if (!queued) return { queued: false, anyPending: false };
        const anyPending = queued.attachedDocIds.some((id) => chatStore.isDocLoading(id));
        return { queued: true, anyPending };
      },
      ({ queued, anyPending }) => {
        if (queued && !anyPending) {
          void chatStore.flushQueuedSend();
        }
      },
      { fireImmediately: true }
    );

    return () => {
      disposed = true;
      stop();
      stopFlushReaction();
      stopPolling();
      if (channel) {
        void supabase.removeChannel(channel);
        channel = null;
      }
    };
  }, [chatStore]);
}
